import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_TARGET_API}`,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");
        if (!refreshToken) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const parsedURL = new URL(window.location.href);
          const subdomain = parsedURL?.hostname?.split(".")?.[0];
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": subdomain === "localhost" ? tenantId : subdomain, // Ensure x-tenant-id is set
          };
          // Request new tokens from `/core/refresh`
          const { data } = await axios.post(
           `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders }
          );

          // Save new tokens in Cookies
          Cookies.set("access_token", data?.access_token, {
            secure: true,
          });
          Cookies.set("refresh_token", data?.refresh_token, {
            secure: true,
          });

          // Retry the original request with the new token
          originalConfig.headers[
            "Authorization"
          ] = `Bearer ${data?.access_token}`;
          originalConfig.headers[
            "Refresh"
          ] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(_error);
        }
      }

      return Promise.reject(err);
    }
  );
};

const axiosBaseQuery =
  () =>
  async (
    { url, method, body, params, ...requestOpts },
    { getState, dispatch }
  ) => {
    setUpInterceptors(getState, dispatch);

    const tenantId = getTenantId();

    const enhancedHeaders = {
      ...requestOpts.headers,
      "X-Tenant-Id": tenantId,
    };

    try {
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: enhancedHeaders,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const targetsettingapi = createApi({
  reducerPath: "targetsettingapi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["targetsetting"],
  endpoints: (builder) => ({
    getToken: builder.mutation({
      query: () => ({
        url: `login`,
        method: "POST",
        body: {
          API_KEY: process.env.REACT_APP_ACTIVITIES_HOST_API_KEY,
        },
      }),
      invalidatesTags: ["activities"],
    }),
    Createtarget: builder.mutation({
      query: ({ payload }) => {
        const tenantId = getTenantId();

        return {
          url: `/create_target/`,
          method: "POST",
          body: payload,
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    GetTargets: builder.query({
      query: ({ token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_targets/`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    DeleteTarget: builder.mutation({
      query: ({ targetNames }) => {
        const tenantId = getTenantId();

        return {
          url: `/delete_target/`,
          method: "POST",
          body: { targetNames },
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    Getcategoriesdata: builder.query({
      query: ({ taxonomy, access_token, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_categories_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            taxonomy,
            access_token,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    Deletecategoriesdata: builder.mutation({
      query: ({ level, access_token, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/delete_category_data/`,
          method: "POST",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          body: {
            level,
            access_token,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
    Getgraphcategoriesdata: builder.query({
      query: ({ taxonomy, node, target, access_token, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_graph_coordinates`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            taxonomy,
            node,
            target,
            access_token,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),

    Getscopecategoriesdata: builder.query({
      query: ({ scope, node, token }) => {
        const tenantId = getTenantId();

        return {
          url: `/get_category_data`,
          method: "GET",
          headers: {
            "X-Tenant-Id": tenantId,
            Authorization: `Bearer ${token}`,
          },
          params: {
            scope,
            node,
          },
        };
      },
      providesTags: ["targetsetting"],
    }),
    SetscopeTarget: builder.mutation({
      query: ({ payload }) => {
        const tenantId = getTenantId();

        return {
          url: `/set_category_data/`,
          method: "POST",
          body: payload,
          headers: {
            "X-Tenant-Id": tenantId,
          },
        };
      },
      invalidatesTags: ["targetsetting"],
    }),
  }),
});

export const {
  useCreatetargetMutation,
  useDeleteTargetMutation,
  useSetscopeTargetMutation,
  useGetTargetsQuery,
  useGetgraphcategoriesdataQuery,
  useGetTokenMutation,
  useGetscopecategoriesdataQuery,
  useDeletecategoriesdataMutation,
  useGetcategoriesdataQuery,
} = targetsettingapi;
